/**
 * チーム状況（Team Pulse）の表示ロジック（純関数、TP-10）。
 *
 * 画面から計算を追い出し、次の 3 点をテストで固定する:
 *   1. 期間の切替で拾う数値が入れ替わること
 *   2. レポートと集計でメンバー表記が揺れても同一人物として結びつくこと
 *   3. 「数値が無い」理由（未接続 / 紐づけ不能 / 計測前）が失われないこと
 */
import type {
  TeamPulseActivity,
  TeamPulseMember,
  TeamPulseProviderCoverage,
} from './api'

/** Split only the stored citation suffix; keep source text intact for disclosure. */
export function splitClaimCitation(value: string): { text: string; citation: string | null } {
  const match = /^([\s\S]+?) \[根拠: ([\s\S]+)\]$/.exec(value)
  return match ? { text: match[1], citation: match[2] } : { text: value, citation: null }
}

export type PeriodKey = 'week' | 'month' | 'quarter'

export const PERIOD_TABS: ReadonlyArray<{ key: PeriodKey; label: string; caption: string }> = [
  { key: 'week', label: '今週', caption: '月曜から今日まで' },
  { key: 'month', label: '今月', caption: '1日から今日まで' },
  { key: 'quarter', label: '3ヶ月', caption: '当月を含む直近3ヶ月' },
]

/** 画面に出す活動量の 1 行 */
export interface ActivityMeasure {
  /** 表示名（経営者が読む言葉。専門用語を出さない） */
  label: string
  /** 数値。null = その情報源が未接続 or 紐づけ不能で「測れていない」 */
  value: number | null
  unit: string
  /** 同じ指標での最大値（棒の長さの基準） */
  max: number
  /** 数値が無いときに理由を示す短い語 */
  absentReason?: string
}

/**
 * 氏名表記のゆらぎを吸収する。
 * レポート側は「大坂さん」、メンバー登録側は「大坂」のように敬称・空白が揺れる。
 */
export function normalizeMemberName(name: string): string {
  return name
    // \u3000 = 全角空白。実データの氏名表記に混ざるため明示的に落とす
    // （リテラルで書くと lint の no-irregular-whitespace に触れるためエスケープで表す）
    .replace(/[\s\u3000]/g, '')
    .replace(/(さん|様|氏)$/u, '')
    .toLowerCase()
}

/** レポートのメンバーと集計のメンバーを突き合わせる（見つからなければ null） */
export function findActivityMember(
  activity: TeamPulseActivity | null,
  memberName: string,
): TeamPulseActivity['members'][number] | null {
  if (!activity) return null
  const target = normalizeMemberName(memberName)
  if (target.length === 0) return null
  return (
    activity.members.find((m) => {
      const candidate = normalizeMemberName(m.displayName)
      if (candidate.length === 0) return false
      return candidate === target || candidate.includes(target) || target.includes(candidate)
    }) ?? null
  )
}

/** 指定期間・指標の値を取り出す（無ければ null。0 と未計測を混同しない） */
export function metricValue(
  member: TeamPulseActivity['members'][number] | null,
  period: PeriodKey,
  provider: string,
  metric: string,
): number | null {
  if (!member) return null
  const hit = member.metrics.find(
    (m) => m.periodType === period && m.provider === provider && m.metric === metric,
  )
  return hit ? hit.value : null
}

/** 情報源が「測れる状態か」を掌握状態から判定する */
export function isMeasurable(
  coverage: TeamPulseProviderCoverage[] | undefined,
  provider: string,
): boolean {
  const hit = coverage?.find((c) => c.provider === provider)
  if (!hit) return false
  return hit.state === 'linked' || hit.state === 'partial'
}

/**
 * TP-25: その人・その情報源が測れる状態かを判定する。
 *
 * 3 つの「数値が無い」を区別する。混ぜると 0 件が稼働評価として一人歩きする。
 *   未登録 — レポートの人物が集計側に見つからない
 *   未計測 — 情報源そのものが未接続 or 紐づけ不能（全員に共通）
 *   対象外 — 情報源は使えるが、その人には紐づけ先が無い（例: SF を使わない役割）
 */
export function providerScope(
  member: TeamPulseActivity['members'][number] | null,
  coverage: TeamPulseProviderCoverage[] | undefined,
  provider: string,
): { ok: true } | { ok: false; reason: string } {
  if (!member) return { ok: false, reason: '未登録' }
  if (!isMeasurable(coverage, provider)) return { ok: false, reason: '未計測' }
  // 旧応答には linkedProviders が無い。判定材料が無いときに「対象外」と断定しない
  if (!member.linkedProviders) return { ok: true }
  return member.linkedProviders.includes(provider)
    ? { ok: true }
    : { ok: false, reason: '対象外' }
}

/**
 * 1 人分の活動量を組み立てる。
 *
 * 測れない情報源は value=null + 理由を持たせ、0 と区別できる形で返す。
 */
export function buildMeasures(
  activity: TeamPulseActivity | null,
  memberName: string,
  period: PeriodKey,
  maxima: {
    meetingDays: number
    utterances: number
    chatworkMessages: number
    slackMessages: number
    sfTasks: number
    sfRecords: number
  },
): ActivityMeasure[] {
  const member = findActivityMember(activity, memberName)
  const coverage = activity?.coverage
  const scopeOf = (provider: string) => providerScope(member, coverage, provider)

  const measure = (
    label: string,
    provider: string,
    metric: string,
    unit: string,
    max: number,
  ): ActivityMeasure => {
    const scope = scopeOf(provider)
    return {
      label,
      value: scope.ok ? (metricValue(member, period, provider, metric) ?? 0) : null,
      unit,
      max: Math.max(max, 1),
      absentReason: scope.ok ? undefined : scope.reason,
    }
  }

  return [
    measure('会議に出た日', 'meeting_speaker', 'meeting_days', '日', maxima.meetingDays),
    measure('会議での発言', 'meeting_speaker', 'utterances', '回', maxima.utterances),
    measure('Chatwork 送信', 'chatwork', 'messages', '件', maxima.chatworkMessages),
    measure('Slack 送信', 'slack', 'messages', '件', maxima.slackMessages),
    measure('SF 作業登録', 'salesforce', 'tasks_created', '件', maxima.sfTasks),
    measure('SF 案件更新', 'salesforce', 'records_updated', '件', maxima.sfRecords),
  ]
}

/** 全メンバー横断の最大値（棒の長さを共通尺度にするため） */
export function computeMaxima(
  activity: TeamPulseActivity | null,
  period: PeriodKey,
): {
  meetingDays: number
  utterances: number
  chatworkMessages: number
  slackMessages: number
  sfTasks: number
  sfRecords: number
} {
  const pick = (provider: string, metric: string) =>
    activity?.members.reduce((max, member) => {
      const value = metricValue(member, period, provider, metric)
      return value !== null && value > max ? value : max
    }, 0) ?? 0

  return {
    meetingDays: pick('meeting_speaker', 'meeting_days'),
    utterances: pick('meeting_speaker', 'utterances'),
    chatworkMessages: pick('chatwork', 'messages'),
    slackMessages: pick('slack', 'messages'),
    sfTasks: pick('salesforce', 'tasks_created'),
    sfRecords: pick('salesforce', 'records_updated'),
  }
}

// ─── 稼働時間帯（TP-18） ───

/** 1 時間ぶんの活動量。value は情報源を合算した件数 */
export interface HourBucket {
  hour: number
  value: number
}

/** 稼働時間帯の要約。数値が薄いときは推計しない */
export interface WorkingHours {
  buckets: HourBucket[]
  /** 活動があった最も早い時刻。データが足りなければ null */
  earliest: number | null
  /** 活動があった最も遅い時刻 */
  latest: number | null
  /** 全時間帯の合計件数 */
  total: number
  /** 曲線として読める最低量に達しているか */
  reliable: boolean
}

/** 時間帯の形を「傾向」と呼べる最低件数。これ未満は点であって曲線ではない */
export const WORKING_HOURS_MIN_SAMPLES = 10

const HOUR_METRIC_RE = /^hour_(\d{2})$/
const DOW_METRIC_RE = /^dow_(\d)$/

/**
 * 刻み別の指標を足し合わせる（時間帯と曜日で同じ処理を共有する）。
 *
 * 会議・Chatwork・Slack を合算する（どの道具かではなく「動いていた時」を見る）。
 * 同じ出来事を 2 通りに刻んでいるだけなので、時間帯と曜日の合計は一致する。
 */
function sumMetricBuckets(
  activity: TeamPulseActivity | null,
  memberName: string,
  period: PeriodKey,
  pattern: RegExp,
  size: number,
  /** 指標の数値を配列の添字に直す（曜日は 1=月 始まりなので 1 ずれる） */
  toIndex: (parsed: number) => number,
): { values: number[]; total: number } {
  const member = findActivityMember(activity, memberName)
  const values = Array.from({ length: size }, () => 0)
  let total = 0

  for (const metric of member?.metrics ?? []) {
    if (metric.periodType !== period) continue
    const matched = pattern.exec(metric.metric)
    if (!matched) continue
    const index = toIndex(Number(matched[1]))
    if (index < 0 || index >= size) continue
    values[index] += metric.value
    total += metric.value
  }

  return { values, total }
}

/**
 * 稼働時間帯を組み立てる。
 *
 * 件数が少ないうちは reliable=false を返し、画面側で断定させない。
 */
export function buildWorkingHours(
  activity: TeamPulseActivity | null,
  memberName: string,
  period: PeriodKey,
): WorkingHours {
  const { values, total } = sumMetricBuckets(
    activity,
    memberName,
    period,
    HOUR_METRIC_RE,
    24,
    (hour) => hour,
  )
  const buckets: HourBucket[] = values.map((value, hour) => ({ hour, value }))

  const active = buckets.filter((b) => b.value > 0)
  return {
    buckets,
    earliest: active.length > 0 ? active[0].hour : null,
    latest: active.length > 0 ? active[active.length - 1].hour : null,
    total,
    reliable: total >= WORKING_HOURS_MIN_SAMPLES,
  }
}

// ─── 曜日別（TP-26） ───

/** 稼働の見方。同じ出来事を「何時か」で見るか「何曜日か」で見るかの切替 */
export type RhythmKey = 'hour' | 'weekday'

export const RHYTHM_TABS: ReadonlyArray<{ key: RhythmKey; label: string; caption: string }> = [
  { key: 'hour', label: '時間帯', caption: '動いていた時間帯' },
  { key: 'weekday', label: '曜日', caption: '動いていた曜日' },
]

/** 表示順は月曜始まり。集計側の dow_1..dow_7（1=月）と対になっている */
export const WEEKDAY_LABELS = ['月', '火', '水', '木', '金', '土', '日'] as const

/** 土日の添字（0 始まりの配列上）。休日稼働の有無を出すために持つ */
const WEEKEND_INDEXES = [5, 6]

export interface WeekdayBucket {
  /** 0=月 … 6=日 */
  index: number
  label: string
  value: number
}

export interface Workdays {
  buckets: WeekdayBucket[]
  /** 最も件数の多い曜日。同数なら早い曜日。データが無ければ null */
  busiest: WeekdayBucket | null
  /** 土日の合計件数 */
  weekend: number
  total: number
  reliable: boolean
}

/** 曜日別の稼働を組み立てる（時間帯と同じ出来事を別の軸で刻んだもの） */
export function buildWorkdays(
  activity: TeamPulseActivity | null,
  memberName: string,
  period: PeriodKey,
): Workdays {
  const { values, total } = sumMetricBuckets(
    activity,
    memberName,
    period,
    DOW_METRIC_RE,
    7,
    // dow_1 = 月曜 → 添字 0
    (dow) => dow - 1,
  )
  const buckets: WeekdayBucket[] = values.map((value, index) => ({
    index,
    label: WEEKDAY_LABELS[index],
    value,
  }))

  const busiest = buckets.reduce<WeekdayBucket | null>(
    (best, b) => (b.value > 0 && (best === null || b.value > best.value) ? b : best),
    null,
  )

  return {
    buckets,
    busiest,
    weekend: WEEKEND_INDEXES.reduce((sum, i) => sum + buckets[i].value, 0),
    total,
    reliable: total >= WORKING_HOURS_MIN_SAMPLES,
  }
}

/** 曜日の棒の高さ（0〜100%）。その人の中の最大値を基準にする */
export function weekdayBarPercent(bucket: WeekdayBucket, buckets: WeekdayBucket[]): number {
  const max = buckets.reduce((m, b) => (b.value > m ? b.value : m), 0)
  if (max <= 0) return 0
  return Math.round((bucket.value / max) * 100)
}

/** 曜日別の 1 行説明。土日に動いているかは判断に効くので必ず出す */
export function workdaysSummary(days: Workdays): string {
  if (days.total === 0) return '記録なし'
  if (!days.reliable) return `記録が少なく傾向は読めない（${days.total} 件）`
  const head = `${days.busiest?.label ?? '—'}曜が最も多い（${days.total} 件）`
  return days.weekend > 0 ? `${head}。土日にも ${days.weekend} 件` : `${head}。土日は記録なし`
}

/** 稼働時間帯の棒の高さ（0〜100%）。その人の中の最大値を基準にする */
export function hourBarPercent(bucket: HourBucket, buckets: HourBucket[]): number {
  const max = buckets.reduce((m, b) => (b.value > m ? b.value : m), 0)
  if (max <= 0) return 0
  return Math.round((bucket.value / max) * 100)
}

/** 稼働時間帯の 1 行説明。断定しない言い方にする */
export function workingHoursSummary(hours: WorkingHours): string {
  if (hours.total === 0) return '記録なし'
  if (!hours.reliable) return `記録が少なく傾向は読めない（${hours.total} 件）`
  return `${hours.earliest}時ごろ〜${hours.latest}時ごろに活動（${hours.total} 件）`
}

/** 棒の長さ（0〜100%）。max=0 でも壊れない */
export function barWidthPercent(value: number | null, max: number): number {
  if (value === null || max <= 0) return 0
  return Math.min(100, Math.round((value / max) * 100))
}

// ─── 掌握状態の表示 ───

export const COVERAGE_LABELS: Record<string, { label: string; hint: string }> = {
  linked: { label: '連携済', hint: '取り込み済みで、誰の分か分かる' },
  partial: { label: '一部', hint: '取り込み済みだが、一部しか人物に紐づかない' },
  unlinked: { label: '紐づけ不可', hint: '取り込み済みだが、誰の分か分からない' },
  absent: { label: '未接続', hint: 'まだ取り込んでいない' },
}

export const PROVIDER_LABELS: Record<string, string> = {
  meeting_speaker: '会議の記録',
  chatwork: 'Chatwork',
  slack: 'Slack',
  github: 'GitHub',
  salesforce: 'Salesforce',
  fieldy_slot: 'Fieldy',
}

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider
}

export function coverageLabel(state: string): { label: string; hint: string } {
  return COVERAGE_LABELS[state] ?? { label: state, hint: '' }
}

/** 掌握状態の並び順: 使える情報源を先に、欠けているものを後に見せる */
const COVERAGE_ORDER: Record<string, number> = {
  linked: 0,
  partial: 1,
  unlinked: 2,
  absent: 3,
}

export function sortCoverage(
  coverage: TeamPulseProviderCoverage[],
): TeamPulseProviderCoverage[] {
  return [...coverage].sort(
    (a, b) => (COVERAGE_ORDER[a.state] ?? 9) - (COVERAGE_ORDER[b.state] ?? 9),
  )
}

/** 余力の見え方（色は意味にだけ使う） */
export type CapacityTone = 'over' | 'full' | 'room' | 'unknown'

export function capacityTone(level: string): CapacityTone {
  if (level.includes('抱えすぎ') || level.includes('過負荷') || level.includes('オーバー')) {
    return 'over'
  }
  if (level.includes('フル') || level.includes('逼迫')) return 'full'
  if (level.includes('余地') || level.includes('余裕')) return 'room'
  return 'unknown'
}

/** レポートのメンバー順を「余力が無い人から」に並べ替える（読む順＝手当ての順） */
export function sortMembersByUrgency(members: TeamPulseMember[]): TeamPulseMember[] {
  const rank: Record<CapacityTone, number> = { over: 0, full: 1, unknown: 2, room: 3 }
  return [...members].sort(
    (a, b) => rank[capacityTone(a.capacity.level)] - rank[capacityTone(b.capacity.level)],
  )
}

/** 更新時刻の読みやすい表示（分単位まで。秒は判断に効かない） */
export function formatUpdatedAt(iso: string | null): string {
  if (!iso) return '未計測'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '未計測'
  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
